import {Component, Injector, Input, OnInit, ViewEncapsulation} from '@angular/core';
import {PermissionService} from '@shared/service/permission.service';
import {NemoModuleKeys, UserPermissionsModel} from '@shared/models/user-permission.model';
import { TranslateContext, TranslatePipe } from '@shared/pipes/translate.pipe';
import { Utilities as U } from '@shared/utilities/utilities.model';
import { logger } from '@app/shared/utilities/logger.model';
import { UploadFileService } from '@app/shared/service/upload-file.service';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material';
import { AppConfigEnvironmentService } from '@app/shared/service/appconfig-environment.service';
import { AppConfigEnvironment } from '@app/shared/models/appconfig-environment.model';
import { Module, ModuleService } from '@app/shared/service/module.service';
import { PROFILE_ADMINISTRATION_USER_MANAGEMENT_ADMIN_KEY, PROFILE_ADMINISTRATION_USER_MANAGEMENT_SUPERADMIN_KEY } from '@app/permissions-variables';
import { AttachedFile } from '@app/shared/models/attached-file.model';

@Component({
	selector: 'amd-download-list',
	templateUrl: './download-list.component.html',
	styleUrls: ['./download-list.component.scss'],
	encapsulation: ViewEncapsulation.None
})
export class DownloadListComponent implements OnInit {


	@Input() page: string;

	files: (FileManual | Guide)[] = [];
	translateContext = TranslateContext;
	env: AppConfigEnvironment;
	userPermission: UserPermissionsModel;
	
	constructor(
		private permissionService: PermissionService,
		private translatePipe: TranslatePipe,
		protected uploadFileService: UploadFileService,
		private appConfig: AppConfigEnvironmentService,
		private moduleService: ModuleService,
		private snackBar: MatSnackBar,
		public injector: Injector
	) {
		this.env = appConfig.Environment;
		this.userPermission = this.permissionService.getCurrentUserPermissions();
	}

	ngOnInit(): void {
		this.loadFiles(this.page);
	}

	private t(key: string, context?: TranslateContext): string {

		return this.translatePipe.transform(key, U.nullish(context, TranslateContext.SHARED));
	}

	private loadFiles(type: string): void {
		this.files = this.getFiles()[ type ];
	}

	private getFiles(): TFileManualMap {

		const comunicazioneReteObdPdf: FileManual = new FileManual({
			href: 'contract-closing/comunicazione_rete_obd.pdf',
			name: this.t('COMUNICAZIONE_RETE_OBD_PDF'),
			size: '146 KB',
		});
		const faqOctoObdPdf: FileManual = new FileManual({
			href: 'contract-closing/faq_octo_obd.pdf',
			name: this.t('FAQ_OCTO_OBD_PDF'),
			size: '146 KB',
		});

		const megaMap: TFileManualMap = {
			'home': [
				new FileManual({href: 'delivery/manuale-consegna.pdf', name: this.t('MANUALE_CONSEGNA'), size: '7.7 MB'}),
				new FileManual({href: 'delivery/criteri_Perizia_Carrozzeria.pdf', name: this.t('CRITERI_PERIZIA_CARROZZERIA'), size: '873 Kb'}),
				new FileManual({href: 'delivery/PROCEDURA SECOND LIFE .pdf', name: this.t('2LIFE_PROCEDURA_OPERATIVA'), size: '562 Kb'}),
				new FileManual({href: 'delivery/manuale_di_installazione_obd.pdf', name: this.t('MANUALE_DI_INSTALLAZIONE_OBD'), size: '3.54 MB'}),
				new FileManual({href: 'delivery/faq_octo.pdf', name: 'FAQ OCTO OBD', size: '233 KB'})
			],
			'delivery-asr': [],
			'authority': [],
			'cc-sp-o-dashboard': [
				new FileManual({href: 'sp/ALD _ NEMO - Manuale d\'uso Riconsegna Centri assistenza.pdf', name: this.t('MANUALE_RICONSEGNA'), size: '6.8 MB'}),
				...[comunicazioneReteObdPdf, faqOctoObdPdf]
			],
			'cc-dk-o-dashboard': [
				new FileManual({href: 'dko/manuale-riconsegna.pdf', name: this.t('MANUALE_RICONSEGNA'), size: '3.1 MB'}),
				...[comunicazioneReteObdPdf, faqOctoObdPdf]
			],
			'cc-dk-dashboard': [
				new FileManual({href: 'dkc/manuale-riconsegna.pdf', name: this.t('MANUALE_RICONSEGNA'), size: '3.2 MB'}),
				...[comunicazioneReteObdPdf, faqOctoObdPdf]
			],
			'cc-dk-bo-dashboard': [
				new FileManual({href: 'dbo/manuale-nemo-riconsegna-backoffice_Dekra.pdf', name: this.t('MANUALE_RICONSEGNA'), size: '2.4 MB'}),
				...[comunicazioneReteObdPdf, faqOctoObdPdf]
			],
			'invoicing': [
				new FileManual({href: 'invoicing/xml esempio meccanica.xml', name: this.t('XML_ESEMPIO_MECCANICA_XML'), size: '3 MB'}), 
				new FileManual({href: 'invoicing/xml esempio carrozzeria.xml', name: this.t('XML_ESEMPIO_CARROZZERIA_XML'), size: '3.82 KB'}), 
				new FileManual({href: 'invoicing/xml esempio approntamento.xml', name: this.t('XML_ESEMPIO_APPRONTAMENTO_XML'), size: '5.88 KB'}), 
				new FileManual({href: 'invoicing/xml esempio fine noleggio.xml', name: this.t('XML_ESEMPIO_FINE_NOLEGGIO_XML'), size: '3.71 KB'}),
			],
			'user-change': [],
			'administration': [				
				// new FileManual({href: 'administration/Manuale Area Amministrativa.pdf', name: "Manuale Area Amministrativa", size: '123 KB'}), 
				new FileManual({href: 'administration/Elenco Job Titles.pdf', name: "Elenco Job Titles", size: '57 KB'}), 
			]
		};		

		if (this.userPermission.isCenterOperator(NemoModuleKeys.INVOICING)) {
			megaMap['invoicing'] = [...megaMap['invoicing'], new FileManual({href: 'invoicing/ALD _ NEMO - Manuale d\'uso Invoicing Centri assistenza v1.1.pdf', name: this.t('MANUALE_FATTURAZIONE'), size: '3.00 MB'}) ];
		}

		if (this.userPermission.profiles.some(profile => profile.key === 'authority_assistance_center')) {
			megaMap['authority'] = [...megaMap['authority'], new FileManual({href: 'authority/ALD _ NEMO - Manuale d\'uso Authority Centri assistenza.pdf', name: this.t('MANUALE_MANUTENZIONE'), size: '6.14 MB'}) ];
		}

		if (this.userPermission.isCenterOperator(NemoModuleKeys.USER_CHNAGE_)) {
			megaMap['user-change'] = [...megaMap['user-change'], new FileManual({href: 'user-change/ALD NEMO_ChangeUser_manuale_nemo-operatore_centro.pdf', name: this.t('MANUALE_OPERATIVO'), size: '5.46 MB'}) ];
		}


		/**
		 * Nuova gestione dei manuali
		 * in futuro bisognerà prenderli direttamente da appConfig tramite un ciclo for che gira i vari moduli dentro appConfig.guides[$module]
		 * 
		 */ 
		if (this.moduleService.getCurrentModule() === Module.ADMINISTRATION) {

			const currentModule = EFileManualModule.ADMINISTRATION;

			if (this.env.guides[currentModule]) {
	
				const guideList = this.env.guides[currentModule];
				
				// Object.keys(guideList).forEach(guide_key => {
				// 	megaMap[currentModule].push( new Guide({...guideList[guide_key], isLink: true, key: guide_key, module: currentModule}) );
				// });
				
				if (this.userPermission.hasProfile([PROFILE_ADMINISTRATION_USER_MANAGEMENT_SUPERADMIN_KEY])) {	
					megaMap[currentModule].push( new Guide({...guideList['administrative_area'], isLink: true, key: 'administrative_area', module: currentModule}) );
				}
				
				if (this.userPermission.hasProfile([PROFILE_ADMINISTRATION_USER_MANAGEMENT_ADMIN_KEY])) {	
					megaMap[currentModule].push( new Guide({...guideList['administrative_admin_area'], isLink: true, key: 'administrative_admin_area', module: currentModule}) );
				}
			}
		}

		return megaMap;
	}

	/**
	 * Scarica i manuali che non sono all'interno del bundle FE
	 * fa prima una chiamata a BE per 
	 * 
	 * @param {Guide} file
	 */ 
	downloadFile(file: Guide): void {

		if (U.isEmpty(file.key)) {
			logger.error('DownloadListComponent ', 'downloadFile ', 'key is empty');
			return;
		}
		
		this.uploadFileService.downloadFileSimple(file);
	}
	
	/**
	 * Mostra messaggio di errore durante il download del file
	 */
	showDownloadFileErrorMessage(): void {
		const config: MatSnackBarConfig = new MatSnackBarConfig();
		config.duration = 2000;
		config.panelClass = ['snack-bar-error'];
		this.snackBar.open(this.translatePipe.transform('IMPOSSIBILE_SCARICARE_L_ALLEGATO', TranslateContext.SHARED), '', config);
	}
}

class FileManual {

	href: string = '/assets/docs/';
	name: string = '';
	size: string = '';
	type: string = '';
	icon: string = 'download-32x32';
	
	isLink: boolean = true;

	ALLOWED_TYPE_LIST = ['pdf', 'xml'];
	
	constructor(obj?: any) {

		if (U.isEmpty(obj)) {
			return;
		}
		
		this.ALLOWED_TYPE_LIST.hasOwnProperty('0');

		if (obj.href) {
			this.setFileType( obj.href.split('.').slice(-1)[0] );
		}
		
		this.name  = U.nullish(obj.name, this.name);
		this.href += U.nullish(obj.href, this.href);
		this.size  = U.nullish(obj.size, this.size);
	}

	private setFileType(type: string): void {

		if (this.isAllowedType(type)) {
			this.type = '.' + type;
		}
	}

	private isAllowedType(type: string): boolean {

		return this.ALLOWED_TYPE_LIST.includes(type);
	}
}

/**
 * 
 *  Rappresenta la nuova entità del manuale
 *  che non viene più memorizzato nel bundle FE
 *  
 *  ma viene fatta una chiamata a BE che restituisce il link
 *  e viene scaricato direttamente, come se fosse un allegato
 *  
 *  quindi non hanno più bisogno del HREF
 *  
 *  in questo modo non si appesantisce il bundle FE
 * 
 * 
 *  PS: creare una superclasse File - o adattarla ad essa se già esistente
 * 
 */ 
export class Guide extends AttachedFile {

	uuid : string = '';
	
	// è la chiave che identifica il manuale. dentro appConfig.json
	key: string = '';

	// nome del file, ma privo di estensione
	name : string = '';

	// nome che verrà mostrato a UI
	label : string = '';

	// dimensione del file, ma in formato stringa
	// perché non è possibile calcolarla dal file
	size : string = '';

	// estensione del file
	type : string = '';
	
	// modulo a cui appartiene il manuale
	module : EFileManualModule;

	// icona da mostrare a UI
	icon : string = 'download-32x32';

	ALLOWED_TYPE_LIST = ['pdf', 'xml'];
	
	constructor(obj?: any) {

		super();

		if (U.isEmpty(obj)) {
			return;
		}

		this.ALLOWED_TYPE_LIST.hasOwnProperty('0');

		this.uuid = obj.uuid || '';
		this.label = obj.label || '';
		this.size = obj.size || '';
		this.type = obj.type || '';
		this.key = obj.key || '';
		this.name = obj.file_name || '';
		this.module = obj.module || '';

		// this.file_name = this.getFileName();

		if (!this.isAllowedType()) {
			throw new Error('Errore durante l\'istanza della classe Guide: tipo di file non consentito');
		}
	}

	/**
	 *  Restituisce il nome del file, compreso di estensione
	 * 
	 *  @returns {string}
	 */ 
	getFileName(): string {
		return this.name + '.' + this.type;
	}

	/**
	 *  Verifica se il tipo di file è consentito
	 *  al momento solo PDF e XML
	 * 
	 *  @param {string} type
	 *  @returns {boolean}
	 */ 
	private isAllowedType(): boolean {
		return this.ALLOWED_TYPE_LIST.includes(this.type);
	}
}

enum EFileManualModule {
	'HOME'							 = 'home',
	'DELIVERY_ASR'			 = 'delivery-asr',
	'AUTHORITY'					 = 'authority',
	'CC_SP_O_DASHBOARD'	 = 'cc-sp-o-dashboard',
	'CC_DK_O_DASHBOARD'	 = 'cc-dk-o-dashboard',
	'CC_DK_DASHBOARD'		 = 'cc-dk-dashboard',
	'CC_DK_BO_DASHBOARD' = 'cc-dk-bo-dashboard',
	'INVOICING'					 = 'invoicing',
	'USER_CHANGE'				 = 'user-change',
	'ADMINISTRATION'		 = 'administration',
};

type TFileManualMap = { [key: string]: (FileManual | Guide)[] };
